import { Mail, Phone, MapPin, Calendar, Trash2, CheckCircle2, Clock, Star, DollarSign } from "lucide-react";
import { prettySchedule } from "../../lib/apptDate";

const statusStyle = {
  new: "bg-clay/15 text-clay",
  contacted: "bg-moss/15 text-moss",
  confirmed: "bg-moss/20 text-moss",
  booked: "bg-charcoal/10 text-charcoal",
  completed: "bg-moss/20 text-moss",
  archived: "bg-stone/15 text-stone",
};

const QUICK = [
  { key: "contacted", label: "Contacted" },
  { key: "completed", label: "Completed" },
  { key: "archived", label: "Archived" },
];

export const BookingCard = ({ a, onConfirm, onSetStatus, onDelete, onRemoveReview }) => (
  <div data-testid={`booking-${a.id}`} className="bg-sand-dark rounded-3xl p-6 md:p-7 border border-charcoal/5 shadow-sm">
    <div className="flex items-start justify-between gap-4">
      <div>
        <h3 className="font-heading text-2xl text-charcoal">{a.name}</h3>
        {a.horse_name ? <p className="text-sm text-stone mt-0.5">Horse: {a.horse_name}</p> : null}
      </div>
      <span className={`text-xs uppercase tracking-wider px-3 py-1.5 rounded-full ${statusStyle[a.status] || statusStyle.new}`}>
        {a.status}
      </span>
    </div>

    <div className="mt-4 space-y-2 text-sm text-stone">
      <p className="flex items-center gap-2"><Mail size={15} strokeWidth={1.5} className="text-clay" /> <a href={`mailto:${a.email}`} className="hover:text-charcoal">{a.email}</a></p>
      <p className="flex items-center gap-2"><Phone size={15} strokeWidth={1.5} className="text-clay" /> <a href={`tel:${a.phone}`} className="hover:text-charcoal">{a.phone}</a></p>
      {a.yard_location ? <p className="flex items-center gap-2"><MapPin size={15} strokeWidth={1.5} className="text-clay" /> {a.yard_location}</p> : null}
      {a.preferred_date ? <p className="flex items-center gap-2"><Calendar size={15} strokeWidth={1.5} className="text-clay" /> Prefers: {a.preferred_date}</p> : null}
      {a.scheduled_at ? <p className="flex items-center gap-2 text-charcoal font-medium"><Clock size={15} strokeWidth={1.5} className="text-clay" /> Scheduled: {prettySchedule(a.scheduled_at)}</p> : null}
      {a.amount > 0 ? <p className="flex items-center gap-2 text-charcoal font-medium"><DollarSign size={15} strokeWidth={1.5} className="text-clay" /> Fee: ${a.amount}</p> : null}
    </div>

    {a.message ? <p className="mt-4 text-sm text-charcoal/80 bg-sand rounded-2xl p-4 leading-relaxed">{a.message}</p> : null}

    {a.review_rating ? (
      <div className="mt-4 bg-sand rounded-2xl p-4 border border-clay/20" data-testid={`review-${a.id}`}>
        <div className="flex items-center gap-1">
          {[1, 2, 3, 4, 5].map((n) => (
            <Star key={n} size={16} strokeWidth={1.5} className={a.review_rating >= n ? "text-clay fill-clay" : "text-charcoal/15"} />
          ))}
          <span className="ml-2 text-xs uppercase tracking-[0.15em] text-clay">Review</span>
          <button
            data-testid={`remove-review-${a.id}`}
            onClick={() => onRemoveReview(a.id)}
            className="ml-auto text-xs text-stone hover:text-red-700 inline-flex items-center gap-1 transition-colors"
          >
            <Trash2 size={13} strokeWidth={1.5} /> Remove
          </button>
        </div>
        {a.review_comment ? <p className="mt-2 text-sm text-charcoal/80 italic leading-relaxed">"{a.review_comment}"</p> : null}
      </div>
    ) : null}

    <p className="mt-3 text-xs text-stone/70">
      Received {new Date(a.created_at).toLocaleString()}
      {a.confirmation_sent ? " · confirmation emailed ✓" : ""}
      {a.thank_you_sent ? " · thank-you emailed ✓" : ""}
    </p>

    <div className="mt-5 flex flex-wrap items-center gap-2 border-t border-charcoal/10 pt-4">
      <button
        data-testid={`confirm-${a.id}`}
        onClick={() => onConfirm(a)}
        className="inline-flex items-center gap-1.5 text-xs px-4 py-2 rounded-full bg-clay text-sand hover:bg-clay-dark transition-colors font-medium"
      >
        <CheckCircle2 size={14} strokeWidth={1.5} /> {a.status === "confirmed" ? "Re-confirm & notify" : "Confirm & notify"}
      </button>
      {QUICK.filter((s) => s.key !== a.status).map((s) => (
        <button
          key={s.key}
          data-testid={`set-${s.key}-${a.id}`}
          onClick={() => onSetStatus(a.id, s.key)}
          className="text-xs px-3 py-1.5 rounded-full border border-charcoal/15 text-stone hover:text-charcoal hover:border-charcoal transition-colors"
        >
          Mark {s.label}
        </button>
      ))}
      <button
        data-testid={`delete-${a.id}`}
        onClick={() => onDelete(a.id)}
        className="ml-auto text-xs px-3 py-1.5 rounded-full text-stone hover:text-red-700 inline-flex items-center gap-1 transition-colors"
      >
        <Trash2 size={14} strokeWidth={1.5} /> Delete
      </button>
    </div>
  </div>
);
